/**
 * Locale-aware date/time formatting hooks
 * Provides formatting functions that respect user's locale preferences
 */

import { useCallback, useMemo } from 'react'
import { DateTime } from 'luxon'
import { useTranslation } from 'react-i18next'
import { useLocale } from './LocaleProvider'
import {
  formatCurrency,
  formatDate,
  formatDateRange,
  formatDateTime,
  formatNumber,
  formatRelativeTime,
  formatTime,
  getLuxonDateFormat,
  getLuxonTimeFormat,
} from './formatters'

// Add missing import

/**
 * Hook providing locale-aware date/time formatting functions
 */
export const useDateTimeFormatters = () => {
  const { locale, is24Hour, dateFormat } = useLocale()

  const formatters = useMemo(
    () => ({
      /**
       * Format a date using user's locale
       */
      date: (date: Date, options?: Intl.DateTimeFormatOptions) =>
        formatDate(date, locale, options),

      /**
       * Format a time using user's locale and time format preference
       */
      time: (date: Date, options?: Intl.DateTimeFormatOptions) =>
        formatTime(date, locale, is24Hour, options),

      /**
       * Format a date and time together
       */
      dateTime: (date: Date, options?: Intl.DateTimeFormatOptions) =>
        formatDateTime(date, locale, is24Hour, options),

      /**
       * Format a date range
       */
      dateRange: (startDate?: Date | null, endDate?: Date | null) =>
        formatDateRange(startDate, endDate, locale, is24Hour),

      /**
       * Format currency
       */
      currency: (amount: number, currency = 'USD') =>
        formatCurrency(amount, locale, currency),

      /**
       * Format number
       */
      number: (value: number) => formatNumber(value, locale),

      /**
       * Format relative time (e.g., "2 days ago")
       */
      relativeTime: (date: Date, baseDate?: Date) =>
        formatRelativeTime(date, locale, baseDate),

      /**
       * Relative for recent dates, absolute once older than `thresholdDays`.
       */
      smartTime: (date: Date, thresholdDays = 30) => {
        const ageInDays =
          (Date.now() - date.getTime()) / (1000 * 60 * 60 * 24)
        if (ageInDays > thresholdDays) {
          return DateTime.fromJSDate(date).toFormat(
            getLuxonDateFormat(dateFormat, true),
          )
        }
        return formatRelativeTime(date, locale)
      },

      /**
       * Format a month + year header (e.g. calendar title)
       */
      monthYear: (date: Date) =>
        formatDate(date, locale, { month: 'long', year: 'numeric' }),

      /**
       * Get Luxon format pattern for dates
       */
      luxonDatePattern: (includeYear = true) =>
        getLuxonDateFormat(dateFormat, includeYear),

      /**
       * Get Luxon format pattern for times
       */
      luxonTimePattern: () => getLuxonTimeFormat(is24Hour),
    }),
    [locale, is24Hour, dateFormat],
  )

  return formatters
}

/**
 * Hook for formatting due dates with relative labels (Today, Tomorrow, etc.)
 */
export const useFormatDueDate = () => {
  const { locale } = useLocale()
  const formatters = useDateTimeFormatters()
  const { t } = useTranslation('common')

  return useCallback(
    (
      value?: Date,
      options: { includeRelative?: boolean } = {},
    ): string => {
      if (!value) {
        return ''
      }

      const dateTime = DateTime.fromJSDate(value)
      const now = DateTime.now()
      const includeRelative = options.includeRelative ?? true

      const includeYear = !dateTime.hasSame(now, 'year')
      const datePart = dateTime.toFormat(formatters.luxonDatePattern(includeYear))
      const hasTime = dateTime.hour !== 0 || dateTime.minute !== 0

      const timeSuffix = hasTime
        ? ` at ${dateTime.toFormat(formatters.luxonTimePattern())}`
        : ''
      const absoluteLabel = `${datePart}${timeSuffix}`

      if (includeRelative) {
        const relativeLabel = getRelativeDueDateLabel(dateTime, now, t)
        if (relativeLabel) {
          return `${relativeLabel} - ${absoluteLabel}`
        }
      }

      return absoluteLabel
    },
    [formatters, locale, t],
  )
}

/**
 * Get relative date label (Today, Tomorrow, Yesterday)
 */
const getRelativeDueDateLabel = (
  dateTime: DateTime,
  now: DateTime,
  t: (key: string) => string,
): string | null => {
  const relativeDays = Math.floor(dateTime.diff(now, 'days').days)

  if (relativeDays === 0) return t('time.today')
  if (relativeDays === 1) return t('time.tomorrow')
  if (relativeDays === -1) return t('time.yesterday')

  return null
}

// Re-export for convenience
export { useLocale } from './LocaleProvider'
